import { FC, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Typography } from "theme";

import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Dialog, DialogTitle } from "@mui/material";

import { useBookingById } from "../../../../api/hooks/useBookingById/useBookingById.ts";
import { useClient } from "../../../../api/hooks/useClient/useClient.ts";
import { useUpdateBooking } from "../../../../api/hooks/useUpdateBooking/useUpdateBooking.ts";
import { InvertButton } from "../../../../theme/components/Button/Button.tsx";
import ControlledTextField from "../../../ControlledInputs/ControlledTextField.tsx";

import { LoyaltyFormScheme, loyaltyScheme } from "./validation.ts";

type LoyaltyProps = {
  customerId: string;
}

const Loyalty: FC<LoyaltyProps> = ({ customerId }) => {
  const [isEditPriceModalOpen, setIsEditPriceModalOpen] = useState(false);
  const { id } = useParams();
  const { refetch } = useBookingById();

  const updateBookingProvider = useUpdateBooking(id || "");

  const { data } = useClient({
    id: customerId
  })

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LoyaltyFormScheme>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(loyaltyScheme),
    defaultValues: {
      loyalty: "0"
    },
  });

  useEffect(() => {
    if (data?.balance){
      setValue('loyalty', String(data.balance))
    }
  }, [data?.balance, setValue]);

  const onSubmit = useCallback(async (data: LoyaltyFormScheme) => {
    try {
      await updateBookingProvider
        .mutateAsync({
          bonusesAmountToDecrease: Number(data.loyalty)
        })
      toast.success("Бронювання успішно оновлено");
      reset();
      setIsEditPriceModalOpen(false);
      await refetch();
    }catch (e: any) {
      toast(e?.message as string);
      console.error(e);
    }
  }, [refetch, reset, updateBookingProvider]);

  return (
    <div>
      <Button onClick={() => setIsEditPriceModalOpen(true)} look="normal">
        %
      </Button>
      <Dialog sx={{
        "& .MuiPaper-root": {
          backgroundColor: "white",
          padding: "16px",
          minWidth: "300px",
        },
      }} open={isEditPriceModalOpen} onClose={() => setIsEditPriceModalOpen(false)}>
        <DialogTitle>Накопичені бонуси</DialogTitle>
        {
          data ? <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate my={2}>
            <Box mb={2}>
              <Typography weight='semiBold' variant="body1">Накопичено: {data?.balance}</Typography>
            </Box>
            <Box mb={2}>
              <ControlledTextField
                control={control}
                required
                name="loyalty"
                label="Бонуси"
                disabled={data?.balance === 0}
                placeholder="Введіть кількість бонусів"
                type="number"
              />
              {errors.loyalty?.message &&
                <Typography color="error" variant="body2">{errors.loyalty.message}</Typography>}
            </Box>

            <InvertButton fullWidth type="submit" disabled={isSubmitting || data?.balance === 0} loading={isSubmitting}>
              Змінити ціну
            </InvertButton>
          </Box> : <></>
        }
      </Dialog>
    </div>
  );
};

export default Loyalty;
